<template>
  <div class="manage-user-page invisible-scrollbar">
    <section class="manage-user-toolbar" :aria-label="$t('management.userActions')">
      <div class="manage-user-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('management.search')"
          :aria-label="$t('management.searchUser')"
          @keydown.enter="SearchDataUser(search)"
        />
        <button type="button" :aria-label="$t('management.search')" @click="SearchDataUser(search)">
          <img src="@/assets/figma/search-normal.svg" alt="" aria-hidden="true" />
        </button>
      </div>
      <button class="create-user" type="button" @click="OpenUserDetailDialog(null, 'Create')">
        {{ $t("management.create") }} +
      </button>
    </section>

    <section class="manage-user-panel">
      <div class="manage-user-table-wrap">
        <v-data-table
          class="manage-user-table"
          :headers="headers"
          :items="ListData"
          :items-per-page="itemsPerPage"
          item-key="userID"
          hide-default-footer
          disable-sort
        >
          <template v-slot:item.no="{ index }">
            {{ no_run + index + 1 }}
          </template>

          <template v-slot:item.fullName="{ item }">
            {{ [item.firstName, item.lastName].filter(Boolean).join(" ") }}
          </template>

          <template v-slot:item.active="{ item }">
            <span :class="['manage-user-status', { inactive: !item.active }]">
              {{ item.active ? $t("management.active") : $t("management.inactive") }}
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="manage-user-actions">
              <button
                type="button"
                :aria-label="$t('management.editNamed', { name: item.userName || $t('management.user') })"
                @click="OpenUserDetailDialog(item.userID, 'edit')"
              >
                <img
                  class="manage-user-action-icon manage-user-action-icon--edit"
                  src="@/assets/icons/brush.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
              <button
                class="delete"
                type="button"
                :aria-label="$t('management.deleteNamed', { name: item.userName || $t('management.user') })"
                :disabled="!item.active"
                @click="InActiveUser(item.userID)"
              >
                <img
                  class="manage-user-action-icon manage-user-action-icon--delete"
                  src="@/assets/icons/bag.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="manage-user-empty">{{ $t("management.noUserData") }}</div>
          </template>
        </v-data-table>
      </div>

      <footer class="manage-user-pagination">
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="3"
          color="#00368e"
          @input="ChangePage(page)"
        />
        <v-select
          v-model="itemsPerPage"
          class="manage-user-page-size"
          :items="items"
          dense
          solo
          flat
          hide-details
          @input="ChangePerPage(itemsPerPage)"
        />
      </footer>
    </section>

    <v-dialog
      v-model="UserDetailDialog"
      persistent
      max-width="792px"
    >
      <v-card class="manage-user-edit-card">
        <header class="manage-user-edit-header">
          <span>{{ EditStatus ? $t("management.editUser") : $t("management.createUser") }}</span>
          <button type="button" :aria-label="$t('management.closeUserDialog')" @click="CloseUserDetailDialog">
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </header>

        <div class="manage-user-edit-body">
          <div class="manage-user-profile-row">
            <div class="manage-user-profile-field">
              <span class="manage-user-profile-label">{{ $t("management.profileImage") }}</span>
              <button
                class="manage-user-profile-picker"
                type="button"
                :aria-label="$t('management.chooseProfileImage')"
                @click="$refs.profileImageInput.click()"
              >
                <img v-if="ProfileImage" :src="ProfileImageSource" :alt="$t('management.profilePreview')" />
                <v-icon v-else color="#00378e" size="52">mdi-account</v-icon>
                <span class="manage-user-profile-camera" aria-hidden="true">
                  <v-icon color="white" size="20">mdi-camera-plus</v-icon>
                </span>
              </button>
              <input
                ref="profileImageInput"
                class="manage-user-profile-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                @change="SelectProfileImage"
              />
              <button
                v-if="ProfileImage"
                class="manage-user-profile-remove"
                type="button"
                @click="RemoveProfileImage"
              >
                {{ $t("management.removeImage") }}
              </button>
            </div>

            <div class="manage-user-profile-name-fields">
              <div class="manage-user-form-field">
                <label for="manage-firstname">{{ $t("management.firstName") }}</label>
                <v-text-field id="manage-firstname" v-model="FirstName" outlined dense hide-details />
              </div>
              <div class="manage-user-form-field">
                <label for="manage-lastname">{{ $t("management.lastName") }}</label>
                <v-text-field id="manage-lastname" v-model="LastName" outlined dense hide-details />
              </div>
            </div>
          </div>

          <div class="manage-user-form-grid">
            <div class="manage-user-form-field">
              <label for="manage-username">{{ $t("management.userName") }}</label>
              <v-text-field id="manage-username" v-model="Username" :disabled="EditStatus" outlined dense hide-details />
            </div>
            <div class="manage-user-form-field">
              <label for="manage-password">{{ $t("management.password") }}</label>
              <v-text-field
                id="manage-password"
                v-model="Password"
                :append-icon="ShowPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                :type="ShowPassword ? 'text' : 'password'"
                outlined
                dense
                hide-details
                :placeholder="EditStatus ? $t('management.passwordEditHint') : ''"
                @click:append="ShowPassword = !ShowPassword"
              />
            </div>
            <div class="manage-user-form-field">
              <label for="manage-email">{{ $t("management.email") }}</label>
              <v-text-field id="manage-email" v-model="Email" outlined dense hide-details />
            </div>
            <div class="manage-user-form-field">
              <label for="manage-role">{{ $t("management.roleName") }}</label>
              <v-select
                id="manage-role"
                v-model="RoleId"
                :items="RoleListData"
                item-text="roleName"
                item-value="roleID"
                outlined
                dense
                hide-details
              />
            </div>
            <div v-if="RoleId == 3" class="manage-user-form-field">
              <label for="manage-roompad">{{ $t("management.roomPad") }}</label>
              <v-select
                id="manage-roompad"
                v-model="RoomPadID"
                :items="RoomPadList"
                item-text="padName"
                item-value="padID"
                outlined
                dense
                hide-details
              />
            </div>
            <template v-if="RoleId == 4">
              <div class="manage-user-form-field">
                <label for="manage-shift-start">{{ $t("management.shiftStartTime") }}</label>
                <v-text-field id="manage-shift-start" ref="shiftStartInput" v-model="ShiftStartTime" type="time" outlined dense hide-details @click="OpenShiftPicker('shiftStartInput')" />
              </div>
              <div class="manage-user-form-field">
                <label for="manage-shift-end">{{ $t("management.shiftEndTime") }}</label>
                <v-text-field id="manage-shift-end" ref="shiftEndInput" v-model="ShiftEndTime" type="time" outlined dense hide-details @click="OpenShiftPicker('shiftEndInput')" />
              </div>
              <div class="manage-user-form-field">
                <label for="manage-location">{{ $t("management.location") }}</label>
                <v-select id="manage-location" v-model="LocationID" :items="LocationList" item-text="locationName" item-value="locationID" outlined dense hide-details />
              </div>
              <div class="manage-user-form-field">
                <label for="manage-zone">{{ $t("management.serviceArea") }}</label>
                <v-select id="manage-zone" v-model="ZoneID" :items="AvailableZoneList" item-text="zoneName" item-value="zoneID" :disabled="!LocationID" outlined dense hide-details />
              </div>
            </template>
          </div>

          <div class="manage-user-active-choice active-status-control" role="radiogroup" :aria-label="$t('management.userStatus')">
            <button :class="{ selected: ActiveStatus === '1' }" type="button" @click="ActiveStatus = '1'">{{ $t("management.active") }}</button>
            <button :class="{ selected: ActiveStatus === '2' }" type="button" @click="ActiveStatus = '2'">{{ $t("management.inactive") }}</button>
          </div>

          <footer class="manage-user-edit-actions">
            <button class="cancel" type="button" @click="CloseUserDetailDialog">{{ $t("management.cancel") }}</button>
            <button class="save" type="button" @click="UpdateDataUser">
              {{ EditStatus ? $t("management.update") : $t("management.create") }}
            </button>
          </footer>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("management.loading") }}</span
        >
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from "axios";
import { reportPageDataUpdated } from "@/utils/pageDataUpdated";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "ManageUser",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      UserDetailDialog: false,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      no_run: 0,

      ListData: [],
      RoleListData: [],
      RoomPadList: [],
      LocationList: [],
      ZoneList: [],

      UserID: null,
      RoleId: 0,
      RoomPadID: null,
      ShiftStartTime: "",
      ShiftEndTime: "",
      LocationID: null,
      ZoneID: null,
      FirstName: "",
      LastName: "",
      Username: "",
      Password: "",
      Email: "",
      ProfileImage: "",
      ShowPassword: false,
      ActiveStatus: null,
      EditStatus: false,
    };
  },
  computed: {
    AvailableZoneList() {
      return this.ZoneList.filter(zone => String(zone.locationID) === String(this.LocationID));
    },
    ProfileImageSource() {
      const image = (this.ProfileImage || "").trim();
      if (!image || image.startsWith("data:image/") || image.startsWith("http://") || image.startsWith("https://") || image.startsWith("blob:")) {
        return image;
      }

      let mimeType = "image/jpeg";
      if (image.startsWith("iVBOR")) mimeType = "image/png";
      else if (image.startsWith("UklGR")) mimeType = "image/webp";
      return `data:${mimeType};base64,${image}`;
    },

    headers() {
      return [
        {
          text: this.$t("message.No"),
          value: "no",
          sortable: false,
          align: "start",
        },
        {
          text: this.$t("message.Username"),
          value: "userName",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.FullName"),
          value: "fullName",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.Email"),
          value: "email",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.RoleName"),
          value: "roleName",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.Status"),
          value: "active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("message.Action"),
          value: "action",
          align: "center",
          sortable: false,
        },
      ];
    },
  },
  watch: {
    RoleId(value) {
      if (value != 3) this.RoomPadID = null;
      if (value != 4) {
        this.ShiftStartTime = "";
        this.ShiftEndTime = "";
        this.LocationID = null;
        this.ZoneID = null;
      }
    },
    LocationID(value) {
      if (!this.ZoneList.some(zone => String(zone.locationID) === String(value) && String(zone.zoneID) === String(this.ZoneID))) this.ZoneID = null;
    },
  },

  async mounted() {
    let self = this;
    await self.GetUserList();
    await self.getDataDropdownRole();
    await self.getDataDropdownRoomPad();
    await self.getDataOrderAreas();
  },

  methods: {
    OpenShiftPicker(refName) {
      const input = this.$refs[refName]?.$el?.querySelector("input");
      if (!input) return;
      if (typeof input.showPicker === "function") input.showPicker();
      else input.focus();
    },

    ChangePage(value) {
      this.page = value;
      this.GetUserList();
    },

    ChangePerPage(value) {
      this.itemsPerPage = value;
      this.page = 1;
      this.GetUserList();
    },

    GetUserList() {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        search: self.search,
        page: self.page,
        perPage: self.itemsPerPage,
      };
      return axios
        .post(`${self.url}User/getDataUsers`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
            self.LoadingDialog = false;
          }
          self.LoadingDialog = false;
        })
        .catch(function (error) {
          self.LoadingDialog = false;
          const message =
            error?.response?.data?.message ||
            error?.message ||
            self.$t("management.connectionError");

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        });
    },

    SearchDataUser(value) {
      this.search = value || "";
      this.page = 1;
      this.GetUserList();
    },

    ValidateUser() {
      if (!this.Username || !this.Username.trim()) return this.$t("management.usernameRequired");
      if (!this.EditStatus && (!this.Password || !this.Password.trim())) return this.$t("management.passwordRequired");
      if (this.Password && this.Password.length < 8) return this.$t("management.passwordMinLength");
      if (!this.FirstName || !this.FirstName.trim()) return this.$t("management.firstNameRequired");
      if (!this.LastName || !this.LastName.trim()) return this.$t("management.lastNameRequired");
      if (this.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.Email.trim())) return this.$t("management.invalidEmail");
      if (!this.RoleId) return this.$t("management.roleRequired");
      if (this.RoleId == 3 && !this.RoomPadID) return this.$t("management.roomPadRequired");
      if (this.RoleId == 4) {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(this.ShiftStartTime) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(this.ShiftEndTime)) return this.$t("management.shiftHoursRequired");
        if (this.ShiftStartTime === this.ShiftEndTime) return this.$t("management.shiftHoursDifferent");
        if (!this.LocationID) return this.$t("management.locationRequired");
        if (!this.ZoneID || !this.AvailableZoneList.some(zone => String(zone.zoneID) === String(this.ZoneID))) return this.$t("management.serviceAreaRequired");
      }
      if (!this.ActiveStatus) return this.$t("management.statusRequired");
      return null;
    },

    CloseUserDetailDialog() {
      this.UserDetailDialog = false;
      this.EditStatus = false;
      this.UserID = null;
      this.Username = "";
      this.Password = "";
      this.ShowPassword = false;
      this.FirstName = "";
      this.LastName = "";
      this.Email = "";
      this.ProfileImage = "";
      this.RoleId = null;
      this.RoomPadID = null;
      this.ShiftStartTime = "";
      this.ShiftEndTime = "";
      this.LocationID = null;
      this.ZoneID = null;
      this.ActiveStatus = null;
    },

    async UpdateDataUser() {
      let self = this;
      const validationMessage = self.ValidateUser();
      if (validationMessage) {
        await Swal.fire({ icon: "warning", width: 500, text: validationMessage });
        return;
      }
      let temp = {
        username: self.Username.trim(),
        password: self.Password || null,
        firstname: self.FirstName.trim(),
        lastname: self.LastName.trim(),
        email: self.Email ? self.Email.trim() : null,
        roleID: self.RoleId,
        roomPadID: self.RoleId == 3 ? self.RoomPadID : null,
        shiftStartTime: self.RoleId == 4 ? self.ShiftStartTime : null,
        shiftEndTime: self.RoleId == 4 ? self.ShiftEndTime : null,
        locationID: self.RoleId == 4 ? self.LocationID : null,
        zoneID: self.RoleId == 4 ? self.ZoneID : null,
        active: self.ActiveStatus === "1",
        profileImage: self.GetProfileImagePayload(),
      };
      if (self.EditStatus) temp.userID = self.UserID;
      self.LoadingDialog = true;
      try {
        const endpoint = self.EditStatus ? "User/updateUser" : "User/addUser";
        const response = await axios.post(`${self.url}${endpoint}`, temp);
        if (response?.data?.status !== 0) throw new Error(response?.data?.message || self.$t("management.saveFailed"));
        const wasCreate = !self.EditStatus;
        const currentUsername = String(self.$cookies.get("Username") || "").trim().toLowerCase();
        if (!wasCreate && temp.username.toLowerCase() === currentUsername) {
          const profileImage = temp.profileImage || "";
          localStorage.setItem("ProfileImage", profileImage);
          window.dispatchEvent(new CustomEvent("profile-image-updated", {
            detail: { profileImage },
          }));
        }
        await Swal.fire({ icon: "success", title: wasCreate ? self.$t("management.created") : self.$t("management.updated"), text: wasCreate ? self.$t("management.createSuccess") : self.$t("management.updateSuccess"), showConfirmButton: false, timer: 1500 });
        self.CloseUserDetailDialog();
        if (wasCreate) self.page = 1;
        await self.GetUserList();
      } catch (error) {
        Swal.fire({
          icon: "error",
          width: 500,
          text: error?.response?.data?.message || error?.message || self.$t("management.connectionError"),
        });
      } finally {
        self.LoadingDialog = false;
      }
    },

    async InActiveUser(userID) {
      if (!userID) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: this.$t("management.confirmInactive"),
        text: this.$t("management.userInactiveText"),
        showCancelButton: true,
        confirmButtonText: this.$t("management.confirm"),
        cancelButtonText: this.$t("management.cancel"),
      });
      if (!confirmation.isConfirmed) return;
      this.LoadingDialog = true;
      try {
        const response = await axios.post(`${this.url}User/deleteUserByID`, { userID });
        if (response?.data?.status !== 0) throw new Error(response?.data?.message || this.$t("management.updateFailed"));
        await Swal.fire({ icon: "success", title: this.$t("management.updated"), text: this.$t("management.updateSuccess"), showConfirmButton: false, timer: 1500 });
        await this.GetUserList();
      } catch (error) {
        Swal.fire({
          icon: "error",
          width: 500,
          text: error?.response?.data?.message || error?.message || this.$t("management.connectionError"),
        });
      } finally {
        this.LoadingDialog = false;
      }
    },

    getDataDropdownRole() {
      let self = this;
      axios
        .get(`${self.url}User/getDataDropdownRole`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.RoleListData = response.data.data;
          }
        })
        .catch(function (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            self.$t("management.connectionError");

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        });
    },

    getDataDropdownRoomPad() {
      let self = this;
      axios
        .get(`${self.url}User/getDataDropdownRoomPad`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.RoomPadList = response.data.data;
          }
        })
        .catch(function (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            self.$t("management.connectionError");

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        });
    },

    async getDataOrderAreas() {
      try {
        const [locations, zones] = await Promise.all([
          axios.get(`${this.url}Facility/getDataDropdownLocation`),
          axios.get(`${this.url}Facility/getDataDropdownZone`),
        ]);
        if (locations.data.status !== 0 || zones.data.status !== 0) throw new Error(this.$t("management.connectionError"));
        this.LocationList = locations.data.data || [];
        this.ZoneList = zones.data.data || [];
      } catch (error) {
        Swal.fire({ icon: "warning", width: 500, text: error?.response?.data?.message || error?.message || this.$t("management.connectionError") });
      }
    },

    OpenUserDetailDialog(userID, eventType) {
      let self = this;
      if (eventType == "Create") {
        self.EditStatus = false;
        self.UserID = null;
        self.Username = "";
        self.Password = "";
        self.FirstName = "";
        self.LastName = "";
        self.Email = "";
        self.ProfileImage = "";
        self.RoleId = null;
        self.RoomPadID = null;
        self.ShiftStartTime = "";
        self.ShiftEndTime = "";
        self.LocationID = null;
        self.ZoneID = null;
        self.ActiveStatus = "1";
        self.UserDetailDialog = true;
      } else {
        self.getDataUsersByUserID(userID);
      }
    },

    getDataUsersByUserID(userID) {
      let self = this;
      if (userID != null) {
        self.LoadingDialog = true;
        let temp = {
          userID: userID
        };
        axios
          .post(`${self.url}User/getDataUsersByUserID`, temp)
          .then(function (response) {
            if (response.data.status == 0) {
              self.EditStatus = true;
              self.UserID = response.data.data.userID;
              self.FirstName = response.data.data.firstName;
              self.LastName = response.data.data.lastName;
              self.Username = response.data.data.userName;
              self.Password = "";
              self.ShowPassword = false;
              self.Email = response.data.data.email;
              self.ProfileImage = response.data.data.profileImage || "";
              self.RoleId = response.data.data.roleID;
              self.RoomPadID = response.data.data.roomPadID;
              self.ShiftStartTime = response.data.data.shiftStartTime || "";
              self.ShiftEndTime = response.data.data.shiftEndTime || "";
              self.LocationID = response.data.data.locationID;
              self.ZoneID = response.data.data.zoneID;
              self.ActiveStatus = response.data.data.active == true ? "1" : response.data.data.active == false ? "2" : null;
              self.UserDetailDialog = true;
            }
          })
          .catch(function (error) {
            const message =
              error?.response?.data?.message ||
              error?.message ||
              self.$t("management.connectionError");

            Swal.fire({
              icon: "warning",
              width: 900,
              text: message,
            });
          })
          .finally(() => {
            self.LoadingDialog = false;
          });
      }
    },

    GetColor(value) {
      if (value == true) {
        return "rgb(92, 184, 92,0.3)";
      } else {
        return "rgb(232, 34, 34,0.3)";
      }
    },

    SelectProfileImage(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({ icon: "warning", width: 500, text: this.$t("management.invalidProfileImage") });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire({ icon: "warning", width: 500, text: this.$t("management.profileImageTooLarge") });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") {
          this.ProfileImage = "";
          return;
        }

        // The API stores only the Base64 payload, not the Data URL prefix.
        this.ProfileImage = reader.result.split(",", 2)[1] || "";
      };
      reader.onerror = () => {
        Swal.fire({ icon: "error", width: 500, text: this.$t("management.profileImageReadFailed") });
      };
      reader.readAsDataURL(file);
    },

    GetProfileImagePayload() {
      const image = (this.ProfileImage || "").trim();
      if (!image) return null;
      if (!image.startsWith("data:image/")) return image;
      return image.split(",", 2)[1] || null;
    },

    RemoveProfileImage() {
      this.ProfileImage = "";
    },

    GetColorText(value) {
      if (value == true) {
        return "rgb(92, 184, 92)";
      } else {
        return "rgb(232, 34, 34)";
      }
    },
  },
};
</script>
<style scoped>
* >>> .v-data-table-header {
  background-color: black;
  color: #ffffff !important;
}
* >>> .v-data-table-header th {
  font-size: 14px !important;
  color: #ffffff !important;
}

@keyframes load {
  0% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
  40% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(0.4);
  }
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.rect2 {
  animation-delay: -1.2s;
}
.rect3 {
  animation-delay: -1.1s;
}
.rect4 {
  animation-delay: -1s;
}
.rect5 {
  animation-delay: -0.9s;
}

.page-title {
  color: #444;
  font-size: 22px;
  margin-left: 1rem;
}

.cell {
  color: #333;
  font-size: 14px;
  padding: 10px 8px;
}

.data-table {
  color: #332f2f;
}

.v-data-table-header th {
  font-weight: 600 !important;
  font-size: 14px !important;
  background-color: #333 !important;
  color: white !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

.rounded-xl {
  border-radius: 16px !important;
}

h1 {
  letter-spacing: 0.5px;
}

.v-text-field input {
  font-size: 15px;
}

.section-header {
  font-size: 16px;
  font-weight: 700;
  color: #1e1e1e;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.rounded-xl {
  border-radius: 16px !important;
}

.v-text-field input,
.v-select input {
  font-size: 15px;
}

.manage-user-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 60px;
  background: #f6f6f6;
}

.manage-user-edit-card {
  min-height: 600px;
  max-height: calc(100vh - 48px);
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 30px !important;
  background: #ffffff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.22) !important;
}

.manage-user-edit-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  min-height: 66px;
  height: 66px;
  align-items: center;
  justify-content: center;
  background: #00378e;
  color: #ffffff;
  font-size: 21px;
  font-weight: 600;
}

.manage-user-edit-header button {
  position: absolute;
  top: 15px;
  right: 22px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  background: transparent;
}

.manage-user-edit-body {
  display: flex;
  min-height: 528px;
  flex-direction: column;
  padding: 26px 36px 38px;
}

.manage-user-profile-row {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 56px;
  margin-bottom: 18px;
}

.manage-user-profile-field {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.manage-user-profile-label {
  align-self: flex-start;
  margin: 0 0 7px 1px;
  color: #353535;
  font-size: 13px;
  font-weight: 500;
}

.manage-user-profile-picker {
  position: relative;
  display: grid;
  width: 168px;
  height: 168px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #dddddd;
  border-radius: 16px;
  background: #f6f6f6;
}

.manage-user-profile-picker:focus-visible {
  outline: 3px solid rgba(0, 55, 142, 0.3);
  outline-offset: 3px;
}

.manage-user-profile-picker img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.manage-user-profile-camera {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 50%;
  background: #00378e;
}

.manage-user-profile-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.manage-user-profile-remove {
  margin-top: 7px;
  border: 0;
  background: transparent;
  color: #812424;
  font-size: 12px;
  text-decoration: underline;
}

.manage-user-profile-name-fields {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
}

.manage-user-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 56px;
}

.manage-user-form-field label {
  display: block;
  margin: 0 0 7px 1px;
  color: #353535;
  font-size: 13px;
  font-weight: 500;
}

.manage-user-form-field ::v-deep .v-input__slot {
  min-height: 50px !important;
  border-radius: 26px !important;
  background: #ffffff !important;
}

.manage-user-form-field ::v-deep fieldset {
  border-color: #dddddd !important;
}

.manage-user-form-field ::v-deep .v-input--is-focused fieldset {
  border-color: #00378e !important;
}

.manage-user-form-field ::v-deep input,
.manage-user-form-field ::v-deep .v-select__selection {
  color: #282828;
  font-size: 14px;
}

.manage-user-form-field ::v-deep .v-input__append-inner .v-icon {
  color: #00378e !important;
}

.manage-user-form-field ::v-deep .v-input--is-disabled fieldset {
  border-color: #e4e4e4 !important;
}

.manage-user-form-field ::v-deep .v-input--is-disabled input {
  color: #595959 !important;
}

.manage-user-active-choice {
  display: flex;
  flex-shrink: 0;
  gap: 22px;
  margin-top: 24px;
}

.manage-user-active-choice button {
  width: 145px;
  height: 50px;
  border: 0;
  border-radius: 26px;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24);
  color: #282828;
  font-size: 14px;
}

.manage-user-active-choice button.selected {
  border: 3px solid #00378e;
  box-shadow: none;
}

.manage-user-edit-actions {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  gap: 22px;
  margin-top: 36px;
}

.manage-user-edit-actions button {
  width: 174px;
  height: 60px;
  border: 0;
  border-radius: 30px;
  font-size: 16px;
}

.manage-user-edit-actions .cancel {
  background: #eeeeee;
  color: #00378e;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.manage-user-edit-actions .save {
  background: #00378e;
  color: #ffffff;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.manage-user-toolbar {
  display: grid;
  grid-template-columns: minmax(420px, 660px) 157px;
  align-items: center;
  justify-content: center;
  gap: 23px;
  margin-bottom: 64px;
}

.manage-user-search {
  display: flex;
  height: 60px;
  align-items: center;
  padding: 7px 9px 8px 36px;
  border-radius: 60px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.manage-user-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 18px;
}

.manage-user-search input::placeholder {
  color: #595959;
  opacity: 1;
}

.manage-user-search button {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.22);
}

.manage-user-search button img {
  display: block;
  width: 23px;
  height: 23px;
}

.create-user {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
}

.manage-user-panel {
  position: relative;
  box-sizing: border-box;
  min-height: 436px;
  padding: 31px 18px 110px;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.manage-user-table-wrap {
  overflow-x: auto;
  border-radius: 30px 30px 0 0;
}

.manage-user-table {
  width: 100%;
  color: #4c4c4c;
  font-size: 15px;
}

.manage-user-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.manage-user-table ::v-deep table {
  min-width: 1250px;
  table-layout: fixed;
}

.manage-user-table ::v-deep .v-data-table-header {
  background: #00378e;
}

.manage-user-table ::v-deep .v-data-table-header th {
  height: 81px;
  padding: 0 20px !important;
  border-bottom: 0 !important;
  color: #ffffff !important;
  font-size: 18px;
  font-weight: 400;
  white-space: nowrap;
}

.manage-user-table ::v-deep tbody td {
  height: 66px;
  padding: 0 20px !important;
  border-bottom: 0 !important;
  color: #4c4c4c;
  font-size: 15px;
}

.manage-user-table ::v-deep th:first-child,
.manage-user-table ::v-deep td:first-child { width: 8%; }

.manage-user-table ::v-deep th:nth-child(2),
.manage-user-table ::v-deep td:nth-child(2) { width: 14%; }

.manage-user-table ::v-deep th:nth-child(3),
.manage-user-table ::v-deep td:nth-child(3) { width: 18%; }

.manage-user-table ::v-deep th:nth-child(4),
.manage-user-table ::v-deep td:nth-child(4) { width: 23%; }

.manage-user-table ::v-deep th:nth-child(5),
.manage-user-table ::v-deep td:nth-child(5) { width: 13%; }

.manage-user-table ::v-deep th:nth-child(6),
.manage-user-table ::v-deep td:nth-child(6) {
  width: 11%;
  text-align: center;
}

.manage-user-table ::v-deep th:last-child,
.manage-user-table ::v-deep td:last-child {
  width: 13%;
  text-align: center;
}

.manage-user-table ::v-deep tbody tr {
  background: #ffffff;
}

.manage-user-table ::v-deep tbody tr:nth-child(even) {
  background: #f6f6f6;
}

.manage-user-table ::v-deep tbody tr:hover {
  background: #eef3fb !important;
}

.manage-user-empty {
  padding: 32px 16px;
  color: #595959;
  text-align: center;
}

.manage-user-status {
  display: inline-flex;
  min-width: 94px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #91d589;
  color: #3f6840;
  font-size: 15px;
}

.manage-user-status.inactive {
  background: #f1aaaa;
  color: #812424;
}

.manage-user-actions {
  display: flex;
  height: 66px;
  align-items: center;
  justify-content: center;
  gap: 26px;
}

.manage-user-actions button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #ff8500;
}

.manage-user-actions button.delete {
  background: #d00000;
}

.manage-user-action-icon {
  display: block;
  object-fit: contain;
}

.manage-user-action-icon--edit {
  width: 18px;
  height: 18px;
}

.manage-user-action-icon--delete {
  width: 24px;
  height: 24px;
}

.manage-user-pagination {
  position: absolute;
  right: 18px;
  bottom: 33px;
  left: 18px;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
}

.manage-user-page-size {
  position: absolute;
  right: 50px;
  width: 133px;
  max-width: 133px;
}

.manage-user-page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25) !important;
}

.manage-user-pagination ::v-deep .v-pagination__item,
.manage-user-pagination ::v-deep .v-pagination__navigation {
  width: 49px;
  min-width: 49px;
  height: 49px;
  border-radius: 10px;
}

@media (max-width: 1200px) {
  .manage-user-page {
    padding-right: 24px;
    padding-left: 24px;
  }

  .manage-user-toolbar {
    grid-template-columns: minmax(320px, 1fr) 157px;
  }
}

@media (max-width: 760px) {
  .manage-user-page {
    padding: 24px 12px;
  }

  .manage-user-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }

  .create-user {
    width: 157px;
    justify-self: end;
  }

  .manage-user-panel {
    padding: 12px 12px 150px;
    border-radius: 16px;
  }

  .manage-user-pagination {
    bottom: 24px;
  }

  .manage-user-page-size {
    right: 50%;
    bottom: -66px;
    transform: translateX(50%);
  }

  .manage-user-edit-card {
    min-height: 560px;
    border-radius: 18px !important;
  }

  .manage-user-edit-body {
    min-height: 488px;
    padding: 24px 18px 28px;
  }

  .manage-user-form-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .manage-user-profile-row {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .manage-user-profile-label {
    align-self: center;
  }

  .manage-user-active-choice {
    gap: 12px;
  }

  .manage-user-active-choice button {
    width: 50%;
  }

  .manage-user-edit-actions {
    gap: 12px;
    margin-top: 48px;
  }

  .manage-user-edit-actions button {
    width: 50%;
  }
}

/* Compact desktop density. */
@media (min-width: 761px) {
  .manage-user-edit-card {
    min-height: 0;
    border-radius: 16px !important;
  }

  .manage-user-edit-header {
    min-height: 59px;
    height: 59px;
    font-size: 19px;
  }

  .manage-user-edit-header button {
    top: 13px;
    right: 20px;
    width: 32px;
    height: 32px;
  }

  .manage-user-edit-body {
    min-height: 0;
    padding: 18px 32px 24px;
  }

  .manage-user-profile-row {
    grid-template-columns: 225px 1fr;
    gap: 50px;
    margin-bottom: 14px;
  }

  .manage-user-profile-picker {
    width: 151px;
    height: 151px;
  }

  .manage-user-profile-name-fields {
    gap: 14px;
  }

  .manage-user-form-grid {
    gap: 14px 50px;
  }

  .manage-user-form-field ::v-deep .v-input__slot {
    min-height: 45px !important;
    border-radius: 23px !important;
  }

  .manage-user-active-choice {
    gap: 20px;
    margin-top: 18px;
  }

  .manage-user-active-choice button {
    width: 131px;
    height: 45px;
    border-radius: 23px;
    font-size: 13px;
  }

  .manage-user-edit-actions {
    gap: 20px;
    margin-top: 24px;
  }

  .manage-user-edit-actions button {
    width: 157px;
    height: 54px;
    border-radius: 27px;
    font-size: 14px;
  }

  .manage-user-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .manage-user-toolbar {
    grid-template-columns: minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 28px;
  }

  .manage-user-search,
  .create-user {
    height: 46px;
  }

  .manage-user-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .manage-user-search input {
    font-size: 15px;
  }

  .manage-user-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .manage-user-search button img {
    width: 18px;
    height: 18px;
  }

  .create-user {
    font-size: 16px;
  }

  .manage-user-panel {
    min-height: 360px;
    padding: 20px 14px 90px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .manage-user-table-wrap {
    border-radius: 12px 12px 0 0;
  }

  .manage-user-table ::v-deep .v-data-table-header th {
    height: 56px;
    padding: 0 16px !important;
    font-size: 15px;
  }

  .manage-user-table ::v-deep tbody td {
    height: 50px;
    padding: 0 16px !important;
    font-size: 13px;
  }

  .manage-user-status {
    min-width: 78px;
    height: 26px;
    font-size: 13px;
  }

  .manage-user-actions {
    height: 50px;
    gap: 16px;
  }

  .manage-user-actions button {
    width: 34px;
    height: 34px;
  }

  .manage-user-pagination {
    right: 14px;
    bottom: 24px;
    left: 14px;
    height: 44px;
  }

  .manage-user-page-size {
    right: 30px;
    width: 112px;
    max-width: 112px;
  }

  .manage-user-page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .manage-user-pagination ::v-deep .v-pagination__item,
  .manage-user-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}
</style>
